import { getGuid } from 'owa-guid';
import type { WindowKey } from 'owa-bizchat-types';
import { getBizChatWindowKey } from './getBizChatWindowKey';
import { isCopilotChatDrawerShown } from '../selectors/isCopilotChatDrawerShown';

interface SidepaneSessionData {
    sidePaneOpenToCloseSessionId: string;
    openedTimestamp: number;
}

const sessionByWindow: Map<WindowKey, SidepaneSessionData> = new Map();

/**
 * Gets the existing sidePaneOpenToCloseSessionId for the given target window without creating a new one.
 * Returns undefined if no session exists.
 */
export function getSidePaneOpenToCloseSessionId(targetWindow: Window): string | undefined {
    const windowKey = getBizChatWindowKey(targetWindow);
    return sessionByWindow.get(windowKey)?.sidePaneOpenToCloseSessionId;
}

/**
 * Gets the time in milliseconds since the sidepane was opened for the given target window.
 * Returns undefined if no session exists.
 */
export function getTimeSinceSidepaneOpened(targetWindow: Window): number | undefined {
    const windowKey = getBizChatWindowKey(targetWindow);
    const sessionData = sessionByWindow.get(windowKey);

    if (!sessionData) {
        return undefined;
    }

    return Date.now() - sessionData.openedTimestamp;
}

/**
 * If the side pane is hidden, clears the existing sidePaneOpenToCloseSessionId and returns the new session id.
 * If it's already open, returns the existing one.
 */
export function getOrRegenerateSidePaneOpenToCloseSessionId(
    targetWindow: Window
): string | undefined {
    if (isCopilotChatDrawerShown(targetWindow)) {
        return getSidePaneOpenToCloseSessionId(targetWindow);
    } else {
        return regenerateSidePaneOpenToCloseSessionId(targetWindow);
    }
}

/**
 * Regenerates the sidePaneOpenToCloseSessionId for the given target window by deleting any existing session and creating a new one.
 */
export function regenerateSidePaneOpenToCloseSessionId(targetWindow: Window): string {
    const windowKey = getBizChatWindowKey(targetWindow);

    // Delete the old session if it exists to ensure we generate a new session id when the side pane is opened again
    sessionByWindow.delete(windowKey);

    const sessionData = {
        sidePaneOpenToCloseSessionId: getGuid(),
        openedTimestamp: Date.now(),
    };
    sessionByWindow.set(windowKey, sessionData);

    return sessionData.sidePaneOpenToCloseSessionId;
}

/**
 * Clears the sidePaneOpenToCloseSessionId for the given target window.
 * Called when pop outs are closed to clean up the store
 */
export function cleanupSidePaneOpenToCloseSessionId(targetWindow: Window): void {
    const windowKey = getBizChatWindowKey(targetWindow);
    sessionByWindow.delete(windowKey);
}

/**
 * Returns a provider closure that yields the BizChat side pane correlation fields
 * (`sidePaneOpenToCloseSessionId`, `timeSinceOpened`) for the given window each time
 * it is invoked.
 *
 * Intended for non-entrypoint scenarios that want to correlate their telemetry with the
 * current BizChat side pane session — e.g., Coaching and Highlight & Rewrite — per the
 * "Adding New BizChat Entrypoints" wiki guidance. Both fields are `undefined` when the
 * pane is not currently open.
 *
 * Use this with `CopilotTelemetrySession`'s `bizChatCorrelationProvider` constructor
 * parameter; the indirection exists to avoid a circular dependency between
 * `owa-copilot-telemetry` and `owa-copilot-chat-drawer-store`.
 */
export function createBizChatCorrelationProvider(targetWindow: Window): () => {
    sidePaneOpenToCloseSessionId: string | undefined;
    timeSinceOpened: number | undefined;
} {
    return () => ({
        sidePaneOpenToCloseSessionId: getSidePaneOpenToCloseSessionId(targetWindow),
        timeSinceOpened: getTimeSinceSidepaneOpened(targetWindow),
    });
}
